import { TargetOutlookPage } from "@/revenue-intelligence/target-outlook/page";
import { TARGET_OUTLOOK_PATH } from "@/revenue-intelligence/target-outlook/copy";
import { ForecastExperiencePage } from "@/revenue-intelligence/forecast-experience/page";
import { FORECAST_EXPERIENCE_PATH } from "@/revenue-intelligence/forecast-experience/copy";
import { PipelineHealthL2Page } from "@/revenue-intelligence/pipeline-health-l2/page";
import { PIPELINE_HEALTH_L2_PATH } from "@/revenue-intelligence/pipeline-health-l2/copy";
import CommissionDashboardPage from "@/dashboard/page";
import { Route, Switch, useLocation, useSearch } from "wouter";
import { lazy, Suspense, useEffect } from "react";
import { Loader } from "@/components/Loader";
import { ReportsPage } from "@/reports/page";
import { PlansPage } from "@/plans/page";
import { EditPlanPage } from "@/plans/edit/page";
import { NotFound } from "@/components/NotFound";
import { QuotasPage } from "@/quotas/page";
import { EmployeesPage } from "@/employees/page";
import { EditQuotaPage } from "@/quotas/edit/page";
import { CurrenciesPage } from "@/currencies/page";
import { DealsPage } from "@/deals/page";
import { IntegrationsPage } from "@/integrations/page";
import { Logout } from "./logout";
import { useAppStore } from "@/stores";
import { TeamsPage } from "@/teams/page";
import { EditTeamPage } from "@/teams/edit/page";
import { BuildPlanPage } from "@/plans/builder/page";
import { RevenueIntelligenceDashboardPage } from "@/revenue-intelligence/dashboard/page";
import { RevenueIntelligencePipelineRisk } from "@/revenue-intelligence/pipeline-risk/page";
import { RevenueIntelligencePipelineCoverage } from "@/revenue-intelligence/pipeline-coverage/page";
import { RevenueIntelligenceDealReviewPage } from "@/revenue-intelligence/deal-review/page";
import { RevenueIntelligenceDealDetailsPage } from "@/revenue-intelligence/deal/page";
import { RevenueIntelligenceForecast } from "@/revenue-intelligence/forecast/page";
import { RevenueIntelligenceHistoryPage } from "@/revenue-intelligence/history/page";
import { RevenueIntelligenceManagementReviewPage } from "@/revenue-intelligence/management-review/page";
import { LegacyForecastDiagnosisRedirect } from "@/revenue-intelligence/forecast-diagnosis/page";
import {
  RevenueIntelligenceStateOfSalesV2Page,
} from "@/revenue-intelligence/state-of-sales-v2/page";
import { RevenueIntelligenceStateOfSalesV2ForecastPage } from "@/revenue-intelligence/state-of-sales-v2/ForecastPage";
import { RevenueIntelligenceStateOfSalesV2PipelineRiskPage } from "@/revenue-intelligence/state-of-sales-v2/PipelineRiskPage";
import { RevenueIntelligenceStateOfSalesV2PipelineHealthPage } from "@/revenue-intelligence/state-of-sales-v2/PipelineHealthPage";
import {
  FORECAST_DIAGNOSIS_PATH,
  REVENUE_INTELLIGENCE_AGENTS_PATH,
  REVENUE_INTELLIGENCE_HOME_PATH,
  STATE_OF_SALES_PATH,
} from "@/revenue-intelligence/lib/revenue-intelligence-routes";
import { hasRevenueIntelligenceProductAccess } from "@/revenue-intelligence/lib/revenue-intelligence-product-access";

const RevenueIntelligenceGrowthEfficiencyV2Page = lazy(async () => {
  const module = await import(
    "@/revenue-intelligence/state-of-sales-v2/GrowthEfficiencyV2Page"
  );
  return { default: module.RevenueIntelligenceGrowthEfficiencyV2Page };
});

function SalesEconomicsRoute() {
  return (
    <Suspense
      fallback={(
        <div className="flex min-h-80 items-center justify-center">
          <Loader size={24} />
        </div>
      )}
    >
      <RevenueIntelligenceGrowthEfficiencyV2Page />
    </Suspense>
  );
}

function LegacySalesEconomicsRedirect() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  useEffect(() => {
    setLocation(
      `/revenue-intelligence/state-of-sales-v2/sales-economics${search ? `?${search}` : ""}`,
      { replace: true },
    );
  }, [search, setLocation]);
  return null;
}

function RevenueIntelligenceHomePage() {
  return null;
}

function RevenueIntelligenceAgentsPage() {
  return null;
}

function LegacyStateOfSalesRedirect() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  useEffect(() => {
    setLocation(`${STATE_OF_SALES_PATH}${search ? `?${search}` : ""}`, {
      replace: true,
    });
  }, [search, setLocation]);
  return null;
}

export const Routes = () => {
  const { currentUser } = useAppStore((state) => state);

  // Role
  const isEmployee = currentUser?.role === "employee"
  // const isManager = currentUser?.role === "manager";
  const isAdmin = currentUser?.role === "admin";
  const isSuperAdmin = currentUser?.role === "super_admin" || currentUser?.is_super_admin || false;

  // Environment
  const isProd = import.meta.env.VITE_SITE_URL === "https://dashboard.revelate.app";
  // const isStaging = import.meta.env.VITE_SITE_URL === "https://staging.revelate.app";
  // const isLocal = import.meta.env.VITE_SITE_URL?.includes("localhost");

  // Access
  const accessCommissionProduct = currentUser?.company?.product_access_commissions || isSuperAdmin;
  const accessCommissionProductPrivilegedRoutes = accessCommissionProduct && (isSuperAdmin || isAdmin);
  const accessRevenueIntelligenceProduct = hasRevenueIntelligenceProductAccess(currentUser);
  const accessDataSection = isAdmin || isSuperAdmin;

  const accessOnlyRevenueIntelligenceProduct = accessRevenueIntelligenceProduct && !accessCommissionProduct;
  const CatchAllPage = accessOnlyRevenueIntelligenceProduct
    ? RevenueIntelligenceHomePage
    : CommissionDashboardPage;

  return (
    <Switch>
      {/* Common */}
      <Route path="/logout" component={Logout} />

      {/* Revenue Intelligence product */}
      {accessRevenueIntelligenceProduct && (
        <>
          <Route path={REVENUE_INTELLIGENCE_HOME_PATH} component={RevenueIntelligenceHomePage} />
          <Route path={REVENUE_INTELLIGENCE_AGENTS_PATH} component={RevenueIntelligenceAgentsPage} />
          <Route path={TARGET_OUTLOOK_PATH} component={TargetOutlookPage} />
          <Route path={FORECAST_EXPERIENCE_PATH} component={ForecastExperiencePage} />
          <Route path={PIPELINE_HEALTH_L2_PATH} component={PipelineHealthL2Page} />
          <Route path={`${PIPELINE_HEALTH_L2_PATH}/coverage`} component={PipelineHealthL2Page} />
          <Route path={`${PIPELINE_HEALTH_L2_PATH}/pipeline-creation`} component={PipelineHealthL2Page} />
          <Route path={`${PIPELINE_HEALTH_L2_PATH}/leakage`} component={PipelineHealthL2Page} />
          <Route path={STATE_OF_SALES_PATH} component={RevenueIntelligenceStateOfSalesV2Page} />
          <Route path={FORECAST_DIAGNOSIS_PATH} component={LegacyForecastDiagnosisRedirect} />
          <Route path="/revenue-intelligence/dashboard" component={RevenueIntelligenceDashboardPage} />
          <Route path="/revenue-intelligence/state-of-sales-v2/forecast" component={RevenueIntelligenceStateOfSalesV2ForecastPage} />
          <Route path="/revenue-intelligence/state-of-sales-v2/pipeline-risk" component={RevenueIntelligenceStateOfSalesV2PipelineRiskPage} />
          <Route path="/revenue-intelligence/state-of-sales-v2/pipeline-health" component={RevenueIntelligenceStateOfSalesV2PipelineHealthPage} />
          <Route path="/revenue-intelligence/state-of-sales-v2/sales-economics" component={SalesEconomicsRoute} />
          <Route path="/revenue-intelligence/state-of-sales-v2/growth-efficiency-2" component={LegacySalesEconomicsRedirect} />
          <Route path="/revenue-intelligence/state-of-sales-v2/growth-efficiency" component={LegacySalesEconomicsRedirect} />
          <Route path="/revenue-intelligence/state-of-sales-v2" component={LegacyStateOfSalesRedirect} />
          <Route path="/revenue-intelligence/forecast" component={RevenueIntelligenceForecast} />
          <Route path="/revenue-intelligence/pipeline-risk" component={RevenueIntelligencePipelineRisk} />
          <Route path="/revenue-intelligence/pipeline-coverage" component={RevenueIntelligencePipelineCoverage} />
          <Route path="/revenue-intelligence/deal-review" component={RevenueIntelligenceDealReviewPage} />
          <Route path="/revenue-intelligence/management-review" component={RevenueIntelligenceManagementReviewPage} />
          <Route path="/revenue-intelligence/deal/:id" component={RevenueIntelligenceDealDetailsPage} />
          <Route path="/revenue-intelligence/history" component={RevenueIntelligenceHistoryPage} />
        </>
      )}


      {/* Data section */}
      {accessDataSection && (
        <>
          <Route path="/currencies" component={CurrenciesPage} />
          <Route path="/deals" component={DealsPage} />
          <Route path="/integrations" component={IntegrationsPage} />
          <Route path="/teams" component={TeamsPage} />
          <Route path="/teams/team/new" component={EditTeamPage} />
          <Route path="/teams/team/:id?/*?" component={EditTeamPage} />
          <Route path="/users/:type?/:id?/*?" component={EmployeesPage} />
        </>
      )}



      {/* Commission product */}
      {accessCommissionProduct && (
        <>
          <Route path="/reports/:type?/:id?/*?" component={ReportsPage} />
          <Route path="/dashboard/:type?/:id?" component={CommissionDashboardPage} />
        </>
      )}
      {accessCommissionProductPrivilegedRoutes && (
        <>
          <Route path="/plans" component={PlansPage} />
          <Route path="/plans/plan/new" component={EditPlanPage} />
          <Route path="/plans/plan/:id?/*?" component={EditPlanPage} />
          <Route path="/quotas" component={QuotasPage} />
          <Route path="/quotas/quota/new" component={EditQuotaPage} />
          <Route path="/quotas/quota/:id?/*?" component={EditQuotaPage} />
        </>
      )}

      {/* Experimental */}
      {!isProd && isSuperAdmin && (
        <>
          <Route path="/plans/plan/build" component={BuildPlanPage} />
          <Route path="/plan-builder" component={BuildPlanPage} />
        </>
      )}

      {/* Catchall */}
      <Route path="/" component={CatchAllPage} />
      <Route>
        <NotFound />
      </Route>

    </Switch>
  );
};
